export const FANTASY_GAME_ID = 'dtfantasy' as const;

export const FANTASY_APPROVED_DETAIL_FIELDS = [
  'player_id',
  'player_name',
  'player_team',
  'player_position',
  'captain_role',
  'formation',
  'team_name',
] as const;

export type FantasyApprovedDetailField = (typeof FANTASY_APPROVED_DETAIL_FIELDS)[number];

export type FantasyLabelInputField =
  | 'link_name'
  | 'link_text'
  | 'toggle_name'
  | 'filter_name'
  | 'filter_value';

export type FantasyEventInputField = FantasyApprovedDetailField | FantasyLabelInputField;

export type FantasyEventName =
  | 'dtfantasy_create_team'
  | 'dtfantasy_transfer'
  | 'dtfantasy_substitution'
  | 'dtfantasy_matches'
  | 'dtfantasy_rules'
  | 'dtfantasy_league'
  | 'dtfantasy_navigation'
  | 'stats';

export type FantasyEventCategory =
  | 'FF - Onboarding'
  | 'FF - Transfers'
  | 'FF - Substitutions'
  | 'FF - Matches'
  | 'FF - How to Play'
  | 'FF - Leagues'
  | 'FF - Navigation'
  | 'FF - Stats';

export type FantasyEventContractSource =
  | 'Gaming Hub Events 3.0 - Gaming - Fantasy'
  | 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required';

export interface FantasyEventContract {
  eventName: FantasyEventName;
  eventCategory: FantasyEventCategory;
  eventAction: 'Click';
  eventLabel: string;
  approvedDetailFields: readonly FantasyApprovedDetailField[];
  requiredDetailFields: readonly FantasyApprovedDetailField[];
  labelInterpolations?: Readonly<Record<string, FantasyEventInputField>>;
  expectedDetailValues?: Readonly<Partial<Record<FantasyApprovedDetailField, string>>>;
  source: FantasyEventContractSource;
}

const defineContract = <TContract extends FantasyEventContract>(contract: TContract) => contract;

/**
 * Phase 1 source of truth for the PDF-defined Fantasy events in the current QA scope.
 *
 * The Phase 2 no-throw boundary consumes this registry, but UI handlers are intentionally not
 * wired to it yet. Connecting game interactions remains a separate, reviewed phase.
 */
export const FANTASY_PDF_EVENT_CONTRACTS = {
  onboardingFormationConfirmed: defineContract({
    eventName: 'dtfantasy_create_team',
    eventCategory: 'FF - Onboarding',
    eventAction: 'Click',
    eventLabel: 'Create team - data toggle - {filter_name}',
    approvedDetailFields: ['formation'],
    requiredDetailFields: ['formation'],
    labelInterpolations: { filter_name: 'formation' },
    source: 'Gaming Hub Events 3.0 - Gaming - Fantasy',
  }),
  onboardingCaptainConfirmed: defineContract({
    eventName: 'dtfantasy_create_team',
    eventCategory: 'FF - Onboarding',
    eventAction: 'Click',
    eventLabel: 'Pick Captain',
    approvedDetailFields: [
      'player_id',
      'player_name',
      'player_team',
      'player_position',
      'captain_role',
    ],
    requiredDetailFields: [
      'player_id',
      'player_name',
      'player_team',
      'player_position',
      'captain_role',
    ],
    expectedDetailValues: { captain_role: 'captain' },
    source: 'Gaming Hub Events 3.0 - Gaming - Fantasy',
  }),
  onboardingViceCaptainConfirmed: defineContract({
    eventName: 'dtfantasy_create_team',
    eventCategory: 'FF - Onboarding',
    eventAction: 'Click',
    eventLabel: 'Pick Captain',
    approvedDetailFields: [
      'player_id',
      'player_name',
      'player_team',
      'player_position',
      'captain_role',
    ],
    requiredDetailFields: [
      'player_id',
      'player_name',
      'player_team',
      'player_position',
      'captain_role',
    ],
    expectedDetailValues: { captain_role: 'vice_captain' },
    source: 'Gaming Hub Events 3.0 - Gaming - Fantasy',
  }),
  onboardingTeamNameConfirmed: defineContract({
    eventName: 'dtfantasy_create_team',
    eventCategory: 'FF - Onboarding',
    eventAction: 'Click',
    eventLabel: 'Name Team',
    approvedDetailFields: ['team_name'],
    requiredDetailFields: ['team_name'],
    source: 'Gaming Hub Events 3.0 - Gaming - Fantasy',
  }),
  transferAddPlayerCard: defineContract({
    eventName: 'dtfantasy_transfer',
    eventCategory: 'FF - Transfers',
    eventAction: 'Click',
    eventLabel: 'Add Player - Player Card',
    approvedDetailFields: ['player_id', 'player_name', 'player_team', 'player_position'],
    requiredDetailFields: ['player_id', 'player_name', 'player_team', 'player_position'],
    source: 'Gaming Hub Events 3.0 - Gaming - Fantasy',
  }),
  transferRemovePlayerCard: defineContract({
    eventName: 'dtfantasy_transfer',
    eventCategory: 'FF - Transfers',
    eventAction: 'Click',
    eventLabel: 'Remove Player - Player Card',
    approvedDetailFields: ['player_id', 'player_name', 'player_team', 'player_position'],
    requiredDetailFields: ['player_id', 'player_name', 'player_team', 'player_position'],
    source: 'Gaming Hub Events 3.0 - Gaming - Fantasy',
  }),
  transferAddPlayerListing: defineContract({
    eventName: 'dtfantasy_transfer',
    eventCategory: 'FF - Transfers',
    eventAction: 'Click',
    eventLabel: 'Add Player - Player Listing',
    approvedDetailFields: ['player_id', 'player_name', 'player_team', 'player_position'],
    requiredDetailFields: ['player_id', 'player_name', 'player_team', 'player_position'],
    source: 'Gaming Hub Events 3.0 - Gaming - Fantasy',
  }),
  transferRemovePlayerListing: defineContract({
    eventName: 'dtfantasy_transfer',
    eventCategory: 'FF - Transfers',
    eventAction: 'Click',
    eventLabel: 'Remove Player - Player Listing',
    approvedDetailFields: ['player_id', 'player_name', 'player_team', 'player_position'],
    requiredDetailFields: ['player_id', 'player_name', 'player_team', 'player_position'],
    source: 'Gaming Hub Events 3.0 - Gaming - Fantasy',
  }),
  transferConfirmed: defineContract({
    eventName: 'dtfantasy_transfer',
    eventCategory: 'FF - Transfers',
    eventAction: 'Click',
    eventLabel: 'Transfers - Confirm',
    approvedDetailFields: [],
    requiredDetailFields: [],
    source: 'Gaming Hub Events 3.0 - Gaming - Fantasy',
  }),
  transferHistoryOpened: defineContract({
    eventName: 'dtfantasy_transfer',
    eventCategory: 'FF - Transfers',
    eventAction: 'Click',
    eventLabel: 'Transfer history',
    approvedDetailFields: [],
    requiredDetailFields: [],
    source: 'Gaming Hub Events 3.0 - Gaming - Fantasy',
  }),
  substitutionConfirmed: defineContract({
    eventName: 'dtfantasy_substitution',
    eventCategory: 'FF - Substitutions',
    eventAction: 'Click',
    eventLabel: 'Substitute - Confirm',
    approvedDetailFields: [],
    requiredDetailFields: [],
    source: 'Gaming Hub Events 3.0 - Gaming - Fantasy',
  }),
  matchCentreLink: defineContract({
    eventName: 'dtfantasy_matches',
    eventCategory: 'FF - Matches',
    eventAction: 'Click',
    eventLabel: 'Match - Match centre - {link_name}',
    approvedDetailFields: [],
    requiredDetailFields: [],
    labelInterpolations: { link_name: 'link_name' },
    source: 'Gaming Hub Events 3.0 - Gaming - Fantasy',
  }),
  matchDetailsExpanded: defineContract({
    eventName: 'dtfantasy_matches',
    eventCategory: 'FF - Matches',
    eventAction: 'Click',
    eventLabel: 'Match - expand',
    approvedDetailFields: [],
    requiredDetailFields: [],
    source: 'Gaming Hub Events 3.0 - Gaming - Fantasy',
  }),
  matchDetailsCollapsed: defineContract({
    eventName: 'dtfantasy_matches',
    eventCategory: 'FF - Matches',
    eventAction: 'Click',
    eventLabel: 'Match - collapse',
    approvedDetailFields: [],
    requiredDetailFields: [],
    source: 'Gaming Hub Events 3.0 - Gaming - Fantasy',
  }),
  howToPlayTextLink: defineContract({
    eventName: 'dtfantasy_rules',
    eventCategory: 'FF - How to Play',
    eventAction: 'Click',
    eventLabel: 'Text link - {link text}',
    approvedDetailFields: [],
    requiredDetailFields: [],
    labelInterpolations: { 'link text': 'link_text' },
    source: 'Gaming Hub Events 3.0 - Gaming - Fantasy',
  }),
} as const;

/**
 * Contracts inferred from established Gaming Hub naming patterns for QA gaps that do not have a
 * Gaming - Fantasy web row in the source PDF. These require matching Tealium mappings from the
 * client; keeping them separate prevents inferred names from being mistaken for approved rows.
 */
export const FANTASY_INFERRED_EVENT_CONTRACTS = {
  primaryNavigation: defineContract({
    eventName: 'dtfantasy_navigation',
    eventCategory: 'FF - Navigation',
    eventAction: 'Click',
    eventLabel: 'Navigation - {link_name}',
    approvedDetailFields: [],
    requiredDetailFields: [],
    labelInterpolations: { link_name: 'link_name' },
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  playerSearch: defineContract({
    eventName: 'dtfantasy_create_team',
    eventCategory: 'FF - Onboarding',
    eventAction: 'Click',
    eventLabel: 'Search players',
    approvedDetailFields: [],
    requiredDetailFields: [],
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  playerProfileOpened: defineContract({
    eventName: 'dtfantasy_transfer',
    eventCategory: 'FF - Transfers',
    eventAction: 'Click',
    eventLabel: 'View Player - Player Card',
    approvedDetailFields: ['player_id', 'player_name', 'player_team', 'player_position'],
    requiredDetailFields: ['player_id', 'player_name', 'player_team', 'player_position'],
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  playerProfileDataToggle: defineContract({
    eventName: 'stats',
    eventCategory: 'FF - Stats',
    eventAction: 'Click',
    eventLabel: 'Player profile - Data toggle - {toggle_name}',
    approvedDetailFields: [],
    requiredDetailFields: [],
    labelInterpolations: { toggle_name: 'toggle_name' },
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  playerProfileFilter: defineContract({
    eventName: 'stats',
    eventCategory: 'FF - Stats',
    eventAction: 'Click',
    eventLabel: 'Player profile - Filter {filter_name} - {filter_value}',
    approvedDetailFields: [],
    requiredDetailFields: [],
    labelInterpolations: {
      filter_name: 'filter_name',
      filter_value: 'filter_value',
    },
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  playerProfileMatchLink: defineContract({
    eventName: 'dtfantasy_matches',
    eventCategory: 'FF - Matches',
    eventAction: 'Click',
    eventLabel: 'Match - Player profile - {link_name}',
    approvedDetailFields: [],
    requiredDetailFields: [],
    labelInterpolations: { link_name: 'link_name' },
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  myTeamDataToggle: defineContract({
    eventName: 'dtfantasy_transfer',
    eventCategory: 'FF - Transfers',
    eventAction: 'Click',
    eventLabel: 'Data toggle - {toggle_name}',
    approvedDetailFields: [],
    requiredDetailFields: [],
    labelInterpolations: { toggle_name: 'toggle_name' },
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  resetPendingTransfers: defineContract({
    eventName: 'dtfantasy_transfer',
    eventCategory: 'FF - Transfers',
    eventAction: 'Click',
    eventLabel: 'Reset transfers',
    approvedDetailFields: [],
    requiredDetailFields: [],
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  rankHistoryOpened: defineContract({
    eventName: 'dtfantasy_transfer',
    eventCategory: 'FF - Transfers',
    eventAction: 'Click',
    eventLabel: 'Rank history',
    approvedDetailFields: [],
    requiredDetailFields: [],
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  leagueJoinedByCode: defineContract({
    eventName: 'dtfantasy_league',
    eventCategory: 'FF - Leagues',
    eventAction: 'Click',
    eventLabel: 'Join League by Code',
    approvedDetailFields: [],
    requiredDetailFields: [],
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  leagueLink: defineContract({
    eventName: 'dtfantasy_league',
    eventCategory: 'FF - Leagues',
    eventAction: 'Click',
    eventLabel: 'League - {link_name}',
    approvedDetailFields: [],
    requiredDetailFields: [],
    labelInterpolations: { link_name: 'link_name' },
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  leagueCreated: defineContract({
    eventName: 'dtfantasy_league',
    eventCategory: 'FF - Leagues',
    eventAction: 'Click',
    eventLabel: 'Create League - Confirm',
    approvedDetailFields: [],
    requiredDetailFields: [],
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  leagueDataToggle: defineContract({
    eventName: 'dtfantasy_league',
    eventCategory: 'FF - Leagues',
    eventAction: 'Click',
    eventLabel: 'Data toggle - {toggle_name}',
    approvedDetailFields: [],
    requiredDetailFields: [],
    labelInterpolations: { toggle_name: 'toggle_name' },
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  leagueSettingsUpdated: defineContract({
    eventName: 'dtfantasy_league',
    eventCategory: 'FF - Leagues',
    eventAction: 'Click',
    eventLabel: 'Update League Settings',
    approvedDetailFields: [],
    requiredDetailFields: [],
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  leaguePlayerRemoved: defineContract({
    eventName: 'dtfantasy_league',
    eventCategory: 'FF - Leagues',
    eventAction: 'Click',
    eventLabel: 'Remove League Player',
    approvedDetailFields: [],
    requiredDetailFields: [],
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  leagueInvite: defineContract({
    eventName: 'dtfantasy_league',
    eventCategory: 'FF - Leagues',
    eventAction: 'Click',
    eventLabel: 'Invite - {link_name}',
    approvedDetailFields: [],
    requiredDetailFields: [],
    labelInterpolations: { link_name: 'link_name' },
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  matchCentreFilter: defineContract({
    eventName: 'dtfantasy_matches',
    eventCategory: 'FF - Matches',
    eventAction: 'Click',
    eventLabel: 'Match centre - Filter - {filter_name} - {filter_value}',
    approvedDetailFields: [],
    requiredDetailFields: [],
    labelInterpolations: {
      filter_name: 'filter_name',
      filter_value: 'filter_value',
    },
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  statsCentreDataToggle: defineContract({
    eventName: 'stats',
    eventCategory: 'FF - Stats',
    eventAction: 'Click',
    eventLabel: 'Data toggle - {toggle_name}',
    approvedDetailFields: [],
    requiredDetailFields: [],
    labelInterpolations: { toggle_name: 'toggle_name' },
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
  statsCentreFilter: defineContract({
    eventName: 'stats',
    eventCategory: 'FF - Stats',
    eventAction: 'Click',
    eventLabel: 'Filter {filter_name} - {filter_value}',
    approvedDetailFields: [],
    requiredDetailFields: [],
    labelInterpolations: {
      filter_name: 'filter_name',
      filter_value: 'filter_value',
    },
    source: 'Inferred from Gaming Hub Events 3.0 patterns - client mapping required',
  }),
} as const;

export const FANTASY_EVENT_CONTRACTS = {
  ...FANTASY_PDF_EVENT_CONTRACTS,
  ...FANTASY_INFERRED_EVENT_CONTRACTS,
} as const;

export type FantasyEventContractKey = keyof typeof FANTASY_EVENT_CONTRACTS;
